import React, { useState } from 'react';
import { Box, Button, Heading, Option, SelectList, Stack, Text } from '@indeed/ifl-components';
import proctorOverridesConfig from './proctorOverrides.json';

// ── Config ──────────────────────────────────────────────────────────────────

// Proctor buckets that meaningfully change the Auto Apply / setup experience.
// Values >= 0 are appended to generated links as `prforceGroups`; -1 (inactive)
// is omitted, so a link only forces the buckets you explicitly opt into.
//
// Panel membership, defaults, and buckets live in `proctorOverrides.json` inside
// this package. Do not import `src/server/proctor/specifications` from here:
// after the JSF move that file lives in `packages/server`, and this package is
// already imported by the server, so the import would be a package cycle.
// `auto_apply_jsf_migration_tst` is deliberately omitted (JSF killswitch).

export interface ProctorOption {
    /** Proctor test name (matches src/server/proctor/specifications). */
    name: string;
    /** Lowest selectable bucket value (typically -1 = inactive). */
    min: number;
    /** Highest selectable bucket value. */
    max: number;
    /** Allowed bucket values from the specification, sorted ascending. */
    bucketValues: number[];
    /** Default bucket value when the page loads. Always one of `bucketValues`. */
    defaultValue: number;
    /** Human-readable bucket legend shown under the field. */
    buckets: string;
}

interface PanelProctor {
    name: string;
    defaultValue: number;
    /** `/test/setup` default when it should differ from `defaultValue`. */
    setupDefaultValue?: number;
    buckets: Record<string, number>;
}

interface ProctorSpecification {
    buckets: Record<string, number>;
}

const formatBucketLegend = (buckets: Record<string, number>): string =>
    Object.entries(buckets)
        .sort(([, left], [, right]) => left - right)
        .map(([label, value]) => `${value} ${label}`)
        .join('\n');

const optionFromSpecification = (
    name: string,
    spec: ProctorSpecification,
    defaultValue: number,
): ProctorOption => {
    const bucketValues = [...new Set(Object.values(spec.buckets))].sort(
        (left, right) => left - right,
    );
    const resolvedDefault = bucketValues.includes(defaultValue)
        ? defaultValue
        : (bucketValues[0] ?? -1);
    return {
        name,
        min: bucketValues[0] ?? -1,
        max: bucketValues[bucketValues.length - 1] ?? -1,
        bucketValues,
        defaultValue: resolvedDefault,
        buckets: formatBucketLegend(spec.buckets),
    };
};

const readBuckets = (buckets: object): Record<string, number> =>
    Object.fromEntries(
        Object.entries(buckets).filter(
            (pair): pair is [string, number] => typeof pair[1] === 'number',
        ),
    );

const panelProctors: PanelProctor[] = proctorOverridesConfig.proctors.map((entry) => {
    const buckets = readBuckets(entry.buckets);
    if (Object.keys(buckets).length === 0) {
        throw new Error(
            `No Proctor buckets for "${entry.name}". Add them to proctorOverrides.json.`,
        );
    }
    return {
        name: entry.name,
        defaultValue: entry.defaultValue,
        setupDefaultValue: 'setupDefaultValue' in entry ? entry.setupDefaultValue : undefined,
        buckets,
    };
});

const optionFromPanel = (entry: PanelProctor, defaultValue: number): ProctorOption =>
    optionFromSpecification(entry.name, { buckets: entry.buckets }, defaultValue);

/** Keep a parsed bucket only when the specification defines it; otherwise the page default. */
const allowedProctorValueOrDefault = (option: ProctorOption, value: number): number =>
    option.bucketValues.includes(value) ? value : option.defaultValue;

/** `/test/rbs` panel — defaults come from `defaultValue` in proctorOverrides.json. */
export const SUPPORTED_PROCTORS: ProctorOption[] = panelProctors.map((entry) =>
    optionFromPanel(entry, entry.defaultValue),
);

/** `/test/setup` panel — uses `setupDefaultValue` when the JSON provides one. */
export const SETUP_PROCTORS: ProctorOption[] = panelProctors.map((entry) =>
    optionFromPanel(entry, entry.setupDefaultValue ?? entry.defaultValue),
);

/** A proctor added ad-hoc via the panel (not part of SUPPORTED_PROCTORS). */
export interface CustomProctor {
    name: string;
    value: number;
}

/** Selectable bucket values offered for custom proctors. -1 leaves it unforced. */
export const CUSTOM_PROCTOR_VALUE_OPTIONS = [-1, 0, 1, 2, 3, 4, 5];

/** Build a `prforceGroups` value (e.g. `auto_apply_main_tog1,rnhp_auto_apply_banner_tst2`)
 * from the selected buckets, including only forced (>= 0) values. */
export const buildProctorParam = (
    values: Record<string, number>,
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): string =>
    options
        .map((proctor) => ({ name: proctor.name, value: values[proctor.name] }))
        .filter((proctor) => Number.isFinite(proctor.value) && proctor.value >= 0)
        .map((proctor) => `${proctor.name}${proctor.value}`)
        .join(',');

/** Join supported + custom forced buckets into a single `prforceGroups` string. */
export const buildPrforceGroups = (
    values: Record<string, number>,
    customProctors: CustomProctor[] = [],
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): string => {
    const customParam = customProctors
        .filter((proctor) => Number.isFinite(proctor.value) && proctor.value >= 0)
        .map((proctor) => `${proctor.name}${proctor.value}`)
        .join(',');
    return [buildProctorParam(values, options), customParam].filter(Boolean).join(',');
};

const parseBucketValue = (raw: string): number => {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? -1 : parsed;
};

/** Split a `prforceGroups` token (`auto_apply_main_tog1`) into name + bucket. */
export const parseProctorToken = (
    token: string,
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): { name: string; value: number } | null => {
    const trimmed = token.trim();
    if (!trimmed) return null;

    const matchingOption = [...options]
        .sort((left, right) => right.name.length - left.name.length)
        .find(
            (option) =>
                trimmed.startsWith(option.name) && /^\d+$/.test(trimmed.slice(option.name.length)),
        );

    if (matchingOption) {
        return {
            name: matchingOption.name,
            value: parseInt(trimmed.slice(matchingOption.name.length), 10),
        };
    }

    const match = trimmed.match(/^(.*)(\d+)$/);
    if (!match || match[1] === '') return null;
    return { name: match[1], value: parseInt(match[2], 10) };
};

export interface ProctorOverridesInitialState {
    proctorValues?: Record<string, number>;
    customProctors?: CustomProctor[];
}

/**
 * Turn a `prforceGroups` search param into panel state.
 * `null` means the param is absent → caller should keep hook defaults.
 * `''` means the param is present but empty: every supported proctor is unforced.
 * A non-empty value with no token `parseProctorToken` accepts is garbage → `undefined`,
 * so page defaults stay in place.
 * Otherwise accepted tokens are forced, every other supported proctor is unforced
 * (`-1`), unknown names are custom, and supported tokens outside the specification
 * fall back to that option's default.
 */
export const proctorStateFromPrforceGroups = (
    prforceGroups: string | null,
    options: ProctorOption[] = SUPPORTED_PROCTORS,
): ProctorOverridesInitialState | undefined => {
    if (prforceGroups === null) return undefined;

    const proctorValues = Object.fromEntries(options.map((option) => [option.name, -1]));
    const customProctors: CustomProctor[] = [];
    const optionsByName = new Map(options.map((option) => [option.name, option]));
    let acceptedToken = false;

    prforceGroups.split(',').forEach((token) => {
        const parsed = parseProctorToken(token, options);
        if (!parsed) return;
        acceptedToken = true;
        const option = optionsByName.get(parsed.name);
        if (option) {
            proctorValues[parsed.name] = allowedProctorValueOrDefault(option, parsed.value);
            return;
        }
        const existingIndex = customProctors.findIndex((proctor) => proctor.name === parsed.name);
        if (existingIndex >= 0) {
            customProctors[existingIndex] = parsed;
        } else {
            customProctors.push(parsed);
        }
    });

    if (prforceGroups !== '' && !acceptedToken) return undefined;

    return { proctorValues, customProctors };
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface ProctorOverridesController {
    options: ProctorOption[];
    proctorValues: Record<string, number>;
    setProctorValue: (name: string, raw: string) => void;
    /** Ad-hoc proctors added via the panel, on top of `options`. */
    customProctors: CustomProctor[];
    /** Add (or, if the name already exists, update) a custom proctor. */
    setCustomProctor: (name: string, value: number) => void;
    removeCustomProctor: (name: string) => void;
    /** Comma-joined forced buckets, ready to drop into a `prforceGroups` param. */
    prforceGroups: string;
}

/** Owns the editable bucket state for a proctor-override panel and derives the
 * `prforceGroups` string. Shared by the test pages so both stay in sync. */
export const useProctorOverrides = (
    options: ProctorOption[] = SUPPORTED_PROCTORS,
    initial?: ProctorOverridesInitialState,
): ProctorOverridesController => {
    const [proctorValues, setProctorValues] = useState<Record<string, number>>(() => ({
        ...Object.fromEntries(options.map((proctor) => [proctor.name, proctor.defaultValue])),
        ...initial?.proctorValues,
    }));
    const [customProctors, setCustomProctors] = useState<CustomProctor[]>(
        () => initial?.customProctors ?? [],
    );

    const setProctorValue = (name: string, raw: string) => {
        const option = options.find((entry) => entry.name === name);
        const parsed = parseBucketValue(raw);
        const nextValue = option ? allowedProctorValueOrDefault(option, parsed) : parsed;
        setProctorValues((prev) => ({ ...prev, [name]: nextValue }));
    };

    const setCustomProctor = (name: string, value: number) => {
        const trimmed = name.trim();
        // Ignore blanks and names that would shadow a predefined proctor.
        if (!trimmed || options.some((proctor) => proctor.name === trimmed)) return;
        setCustomProctors((prev) => [
            ...prev.filter((proctor) => proctor.name !== trimmed),
            { name: trimmed, value },
        ]);
    };

    const removeCustomProctor = (name: string) => {
        setCustomProctors((prev) => prev.filter((proctor) => proctor.name !== name));
    };

    const prforceGroups = buildPrforceGroups(proctorValues, customProctors, options);

    return {
        options,
        proctorValues,
        setProctorValue,
        customProctors,
        setCustomProctor,
        removeCustomProctor,
        prforceGroups,
    };
};

// ── Panel ──────────────────────────────────────────────────────────────────────

interface ProctorOverridesPanelProps {
    controller: ProctorOverridesController;
    /** Short blurb explaining where the forced buckets end up. */
    description?: React.ReactNode;
    /** Fired with the next `prforceGroups` after a field is committed (select / add / remove). */
    onCommit?: (prforceGroups: string) => void;
}

/** Sticky side panel of spec-limited selects, one per supported proctor, plus a live
 * `prforceGroups` preview. Presentational — state lives in `useProctorOverrides`. */
export const ProctorOverridesPanel: React.FC<ProctorOverridesPanelProps> = ({
    controller,
    description,
    onCommit,
}) => {
    const {
        options,
        proctorValues,
        setProctorValue,
        customProctors,
        setCustomProctor,
        removeCustomProctor,
        prforceGroups,
    } = controller;

    // Local state for the "add proctor" form.
    const [newName, setNewName] = useState('');
    const [newValue, setNewValue] = useState(1);

    const commitWith = (
        nextValues: Record<string, number> = proctorValues,
        nextCustom: CustomProctor[] = customProctors,
    ) => {
        onCommit?.(buildPrforceGroups(nextValues, nextCustom, options));
    };

    const customProctorsWithValue = (name: string, value: number): CustomProctor[] =>
        customProctors.map((entry) => (entry.name === name ? { ...entry, value } : entry));

    const handleAdd = () => {
        const trimmed = newName.trim();
        setCustomProctor(newName, newValue);
        setNewName('');
        setNewValue(1);
        if (!trimmed || options.some((proctor) => proctor.name === trimmed)) return;
        commitWith(proctorValues, [
            ...customProctors.filter((proctor) => proctor.name !== trimmed),
            { name: trimmed, value: newValue },
        ]);
    };

    return (
        <Box
            sx={{
                flex: '1 1 18rem',
                maxInlineSize: '22rem',
                position: 'sticky',
                insetBlockStart: 8,
                marginBlockStart: 8,
                padding: 4,
                border: '1px solid',
                borderColor: 'border.weak',
                borderRadius: 'md',
                backgroundColor: 'background.weaker',
                fontFamily: 'mono',
            }}
        >
            <Heading level={3} sx={{ marginBlockEnd: 2 }}>
                Proctor overrides
            </Heading>
            <Text variant="secondary" sx={{ marginBlockEnd: 4, display: 'block', fontSize: 'sm' }}>
                {description ?? (
                    <>
                        Forced buckets are appended to the generated links as{' '}
                        <code>prforceGroups</code>. Set a proctor to <code>-1</code> to leave it
                        unforced.
                    </>
                )}
            </Text>

            <Stack spacing={4} vertical>
                {options.map((proctor) => (
                    <Box key={proctor.name}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 3,
                            }}
                        >
                            <label
                                htmlFor={`proctor-${proctor.name}`}
                                style={{
                                    display: 'block',
                                    minInlineSize: 0,
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    fontWeight: 700,
                                    wordBreak: 'break-all',
                                }}
                            >
                                {proctor.name}
                            </label>
                            <Box sx={{ flexShrink: 0, width: 'clamp(7rem, 40%, 10rem)' }}>
                                <SelectList
                                    id={`proctor-${proctor.name}`}
                                    aria-label={proctor.name}
                                    size="sm"
                                    popupProps={{
                                        sx: {
                                            minInlineSize: '7rem',
                                        },
                                    }}
                                    selectedValues={[
                                        String(
                                            allowedProctorValueOrDefault(
                                                proctor,
                                                proctorValues[proctor.name],
                                            ),
                                        ),
                                    ]}
                                    onSelectionChange={(selectedValues) => {
                                        const nextValue = allowedProctorValueOrDefault(
                                            proctor,
                                            parseBucketValue(String(selectedValues[0] ?? '')),
                                        );
                                        setProctorValue(proctor.name, String(nextValue));
                                        commitWith({
                                            ...proctorValues,
                                            [proctor.name]: nextValue,
                                        });
                                    }}
                                >
                                    {proctor.bucketValues.map((value) => (
                                        <Option
                                            key={value}
                                            value={String(value)}
                                            sx={{ whiteSpace: 'nowrap' }}
                                        >
                                            {String(value)}
                                        </Option>
                                    ))}
                                </SelectList>
                            </Box>
                        </Box>
                        <Text
                            variant="secondary"
                            sx={{
                                display: 'block',
                                fontSize: 'xs',
                                marginBlockStart: 1,
                                whiteSpace: 'pre-line',
                            }}
                        >
                            {proctor.buckets}
                        </Text>
                    </Box>
                ))}

                {customProctors.map((proctor) => (
                    <Box
                        key={proctor.name}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 3,
                        }}
                    >
                        <Box sx={{ minInlineSize: 0 }}>
                            <Text
                                sx={{
                                    display: 'block',
                                    fontFamily: 'mono',
                                    fontSize: 'sm',
                                    fontWeight: '700',
                                    wordBreak: 'break-all',
                                }}
                            >
                                {proctor.name}
                            </Text>
                            <Text
                                variant="secondary"
                                sx={{ display: 'block', fontSize: 'xs', marginBlockStart: 1 }}
                            >
                                custom · -1 unforced
                            </Text>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                            <select
                                aria-label={`${proctor.name} value`}
                                value={proctor.value}
                                onChange={(e) => {
                                    const nextValue = parseInt(e.target.value, 10);
                                    setCustomProctor(proctor.name, nextValue);
                                    commitWith(
                                        proctorValues,
                                        customProctorsWithValue(proctor.name, nextValue),
                                    );
                                }}
                                onBlur={(e) => {
                                    const nextValue = parseInt(e.target.value, 10);
                                    commitWith(
                                        proctorValues,
                                        customProctorsWithValue(proctor.name, nextValue),
                                    );
                                }}
                                style={{ fontFamily: 'monospace', fontSize: '13px' }}
                            >
                                {CUSTOM_PROCTOR_VALUE_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                aria-label={`Remove ${proctor.name}`}
                                onClick={() => {
                                    removeCustomProctor(proctor.name);
                                    commitWith(
                                        proctorValues,
                                        customProctors.filter(
                                            (entry) => entry.name !== proctor.name,
                                        ),
                                    );
                                }}
                                style={{
                                    fontFamily: 'monospace',
                                    fontSize: '13px',
                                    cursor: 'pointer',
                                    lineHeight: 1,
                                }}
                            >
                                ×
                            </button>
                        </Box>
                    </Box>
                ))}
            </Stack>

            <Box
                sx={{
                    marginBlockStart: 4,
                    paddingBlockStart: 3,
                    borderBlockStart: '1px solid',
                    borderColor: 'border.weak',
                }}
            >
                <Text
                    sx={{ display: 'block', fontSize: 'sm', fontWeight: '700', marginBlockEnd: 2 }}
                >
                    Add proctor
                </Text>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <input
                        aria-label="New proctor name"
                        type="text"
                        value={newName}
                        placeholder="proctor_name"
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAdd();
                        }}
                        style={{
                            flex: '1 1 auto',
                            minInlineSize: 0,
                            fontFamily: 'monospace',
                            fontSize: '13px',
                        }}
                    />
                    <select
                        aria-label="New proctor value"
                        value={newValue}
                        onChange={(e) => setNewValue(parseInt(e.target.value, 10))}
                        style={{ flexShrink: 0, fontFamily: 'monospace', fontSize: '13px' }}
                    >
                        {CUSTOM_PROCTOR_VALUE_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        ))}
                    </select>
                    <Button
                        variant="secondary"
                        size="sm"
                        disabled={!newName.trim()}
                        onClick={handleAdd}
                    >
                        Add
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    marginBlockStart: 4,
                    paddingBlockStart: 3,
                    borderBlockStart: '1px solid',
                    borderColor: 'border.weak',
                }}
            >
                <Text sx={{ display: 'block', fontSize: 'sm', fontWeight: '700' }}>
                    prforceGroups
                </Text>
                <Text
                    variant="secondary"
                    data-testid="proctor-prforce-preview"
                    sx={{ display: 'block', fontSize: 'sm', wordBreak: 'break-all' }}
                >
                    {prforceGroups || '(none forced)'}
                </Text>
            </Box>
        </Box>
    );
};
